/**
 * Giving a vehicle back to the seller it was bought from.
 *
 * Winnie, 18 September: "a significant issue was found with it after acquisition — an
 * odometer discrepancy is the confirmed example, but any material defect not disclosed at
 * purchase fits the same shape."
 *
 * Three of her decisions shape this file more than the data model does:
 *
 * 1. **No new status.** "Rather than inventing a returned_to_seller entry in StatusRegistry
 *    (which would recreate exactly the duplication problem the registry exists to prevent),
 *    this reuses the existing Cancelled move-stage action, with VehicleReturn as the
 *    structured record behind *why*."
 * 2. **Scoped to before the vehicle is sold.** A defect found after a sale is a dispute with
 *    the wholesale buyer, not a return — so a vehicle with a live outflow deal is refused
 *    here rather than half-handled.
 * 3. **The GST reversal is automated, and it posts in the CURRENT period.** Her reasoning is
 *    worth keeping in front of whoever changes this next: a *correction* means the original
 *    figure was wrong and might need an already-lodged period touched, which needs an
 *    accountant. A *return* is a new event — the original claim was correct when it was made —
 *    so the reversal belongs in the period the return happened, exactly like a credit note,
 *    and never touches the original purchase period. That is what makes it safe to automate.
 *
 * Commission needs no cleanup: it is only ever struck at Sold, and a return happens before
 * that, so there is never a record to void.
 */
import { supabase } from '../lib/supabase'
import { User } from '../types'
import { transitionDealStatus } from '../lib/dealStatusService'
import { SOLD_STATUSES } from '../lib/dealLifecycle'
import { retryDischarge } from './ppsrDischargeService'
import { ACTIONS, logAction } from './actionLog'

export type ReturnIssueType = 'OdometerDiscrepancy' | 'UndisclosedDamage' | 'TitleIssue' | 'Other'

export const ISSUE_LABEL: Record<ReturnIssueType, string> = {
  OdometerDiscrepancy: 'Odometer discrepancy',
  UndisclosedDamage: 'Undisclosed damage',
  TitleIssue: 'Title issue',
  Other: 'Other',
}

export const ISSUE_TYPES: ReturnIssueType[] = [
  'OdometerDiscrepancy',
  'UndisclosedDamage',
  'TitleIssue',
  'Other',
]

/**
 * The statuses that mean an outflow deal already exists against this vehicle.
 *
 * The sold half comes from the canonical list rather than being written out again here - the
 * status registry exists so a second hand-maintained copy cannot drift from it, and the QA
 * guard catches exactly this. `deal_pending` and `closed` are named because they are not sold
 * statuses but do mean a sale is under way or finished.
 */
const BLOCKING_STATUSES = ['deal_pending', 'closed', ...SOLD_STATUSES]

export interface VehicleReturn {
  id: string
  dealId: string
  issueType: ReturnIssueType
  description: string
  loggedByName: string
  returnedToSellerAt: string
  refundAmountExpected: number | null
  refundReceivedAt: string | null
}

const toReturn = (r: Record<string, unknown>): VehicleReturn => ({
  id: String(r.id),
  dealId: String(r.deal_id),
  issueType: r.issue_type as ReturnIssueType,
  description: String(r.description || ''),
  loggedByName: String(r.logged_by_name || '—'),
  returnedToSellerAt: String(r.returned_to_seller_at || ''),
  refundAmountExpected: r.refund_amount_expected == null ? null : Number(r.refund_amount_expected),
  refundReceivedAt: (r.refund_received_at as string) || null,
})

export async function loadReturn(dealId: string): Promise<VehicleReturn | null> {
  const { data, error } = await supabase
    .from('vehicle_returns')
    .select('*')
    .eq('deal_id', dealId)
    .order('returned_to_seller_at', { ascending: false })
    .limit(1)
  if (error) throw error
  const row = (data || [])[0] as Record<string, unknown> | undefined
  return row ? toReturn(row) : null
}

export interface ReturnResult {
  ok: boolean
  message: string
}

export async function returnVehicleToSeller(args: {
  dealId: string
  issueType: ReturnIssueType
  description: string
  refundAmountExpected?: number | null
  user: User
}): Promise<ReturnResult> {
  const description = String(args.description || '').trim()
  if (!description) {
    return { ok: false, message: 'Describe the issue. The return is only useful as a record if it says what was wrong.' }
  }

  const { data, error } = await supabase
    .from('deals')
    .select('id, deal_status, autograb_vin, vehicle_vin, gst_treatment, seller_type, valuation_amount, acquired_date, paid_at, created_at')
    .eq('id', args.dealId)
    .maybeSingle()
  if (error) throw error
  const deal = data as Record<string, unknown> | null
  if (!deal) return { ok: false, message: 'That vehicle could not be loaded.' }

  const status = String(deal.deal_status || '')
  if (BLOCKING_STATUSES.includes(status)) {
    return {
      ok: false,
      message:
        'There is already an outflow deal against this vehicle. Cancel or void that sale first — a return cannot ' +
        'happen underneath an active sale, and a defect found after it sold is a dispute with the buyer rather than ' +
        'a return to the original seller.',
    }
  }

  const vin = String(deal.autograb_vin || deal.vehicle_vin || '')
  const notes: string[] = []

  const { data: inserted, error: insErr } = await supabase
    .from('vehicle_returns')
    .insert([
      {
        deal_id: args.dealId,
        vin,
        issue_type: args.issueType,
        description,
        logged_by: args.user?.id || null,
        logged_by_name: args.user?.full_name || args.user?.email || null,
        refund_amount_expected: args.refundAmountExpected ?? null,
      },
    ])
    .select('id')
    .single()
  if (insErr) throw insErr
  const returnId = (inserted as { id: string }).id

  /* Every cost against the vehicle is voided, not deleted — the same void pattern Cost of
     Sales already uses, so the lines stay auditable and drop out of every total. */
  const { error: costErr } = await supabase
    .from('expenses')
    .update({
      /* Void is a `payment_status`, not a column of its own — that is how Cost of Sales
         voids a line, and using a second mechanism here would give the platform two ideas of
         what a voided cost is. */
      payment_status: 'void',
    })
    .eq('deal_id', args.dealId)
    .neq('payment_status', 'void')
  if (costErr) notes.push('the cost lines could not all be voided')

  /* Direct Auto is giving the vehicle up, so it releases its own registered interest. Same
     API path as the outflow discharge; a failure is reported, not swallowed. */
  try {
    const d = await retryDischarge(args.dealId)
    if (d.attempted && !d.discharged) {
      notes.push('the PPSR discharge failed and needs a retry from Reports → PPSR Register')
    }
  } catch {
    notes.push('the PPSR discharge could not be started')
  }

  const reversal = await postGstReversalIfNeeded(deal, returnId, vin)
  if (reversal) notes.push(reversal)

  const moved = await transitionDealStatus(
    args.dealId,
    status as never,
    'cancelled' as never,
    args.user?.id || undefined,
    args.user,
    'Returned to seller — ' + ISSUE_LABEL[args.issueType] + ': ' + description,
  )
  if (!moved.success) notes.push('the vehicle status could not be moved to Cancelled (' + moved.message + ')')

  await logAction({
    dealId: args.dealId,
    action: ACTIONS.VEHICLE_RETURNED_TO_SELLER,
    actorId: args.user?.id || null,
    details: { issue_type: args.issueType, description, refund_expected: args.refundAmountExpected ?? null },
  })

  return {
    ok: true,
    message:
      'Recorded as returned to the seller. The vehicle is cancelled, its cost lines are voided and retained, and any ' +
      'PPSR registration has been released.' + (notes.length ? ' Note: ' + notes.join('; ') + '.' : ''),
  }
}

/**
 * The GST credit reversal, for a vehicle bought on a tax invoice.
 *
 * Winnie: "For a TaxInvoiceProvided acquisition, the GST credit was claimed uncapped, at the
 * purchase period... Returning the vehicle means that claim needs to be unwound."
 *
 * The notional-ITC path needs nothing: the margin scheme credit on an unsold vehicle is
 * already nil, so there is nothing that was ever claimed to reverse.
 *
 * Returns a note when something went wrong, or null when there was nothing to do or it worked.
 */
async function postGstReversalIfNeeded(
  deal: Record<string, unknown>,
  returnId: string,
  vin: string,
): Promise<string | null> {
  const treatment = String(deal.gst_treatment || '')
  if (treatment !== 'tax_invoice') return null

  /* The credit claimed on a tax-invoice acquisition is the purchase price, uncapped, and the
     GST inside it is one eleventh. Both are taken from the same figures the register reads,
     so the reversal cannot disagree with the original claim. */
  const purchasePrice = Number(deal.valuation_amount) || 0
  if (purchasePrice <= 0) return 'no purchase price was recorded, so no GST reversal was posted'

  const today = new Date().toISOString().slice(0, 10)
  const { error } = await supabase.from('gst_reversal_entries').insert([
    {
      deal_id: String(deal.id),
      vin,
      linked_return_id: returnId,
      reversal_amount: -purchasePrice,
      notional_credit_reversal: Math.round((-purchasePrice / 11) * 100) / 100,
      period_date: today,
    },
  ])
  if (error) return 'the GST reversal could not be posted (' + error.message + ')'
  return null
}

export interface GstReversalRow {
  id: string
  dealId: string
  vin: string
  reversalAmount: number
  notionalCreditReversal: number
  periodDate: string
  label: string
}

/** Reversal lines for the Margin Scheme register, for the period being shown. */
export async function loadGstReversals(): Promise<GstReversalRow[]> {
  const { data, error } = await supabase
    .from('gst_reversal_entries')
    .select('*')
    .order('period_date', { ascending: false })
  if (error) throw error
  return ((data || []) as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r.id),
    dealId: String(r.deal_id),
    vin: String(r.vin || ''),
    reversalAmount: Number(r.reversal_amount) || 0,
    notionalCreditReversal: Number(r.notional_credit_reversal) || 0,
    periodDate: String(r.period_date || ''),
    label: String(r.label || 'Return — GST reversal'),
  }))
}
